import React, { FC, ReactNode, Ref, createContext, useEffect, useState } from "react";

import useBreakPoint from "@/hooks/useBreakpoint";

interface Props {
  children: ReactNode;
}
interface NavigationContextProps {
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
  transitionComplete: boolean;
  setTransitionComplete: (complete: boolean) => void;
  settingsVisible: boolean;
  setSettingsVisible: (open: boolean) => void;
  languagesVisible: boolean;
  setLanguagesVisible: (open: boolean) => void;
  mobilePanelID: string;
  setMobilePanelID: (panel: string) => void;
  desktopPanelID: string;
  setDesktopPanelID: (panel: string) => void;
  mobileHeaderHeight: number;
  setMobileHeaderHeight: (size: number) => void;
  desktopHeaderHeight: number;
  setDesktopHeaderHeight: (size: number) => void;
}

export const NavigationContext = createContext<NavigationContextProps>({
  mobileMenuOpen: false,
  setMobileMenuOpen: () => {},
  transitionComplete: false,
  setTransitionComplete: () => {},
  settingsVisible: false,
  setSettingsVisible: () => {},
  languagesVisible: false,
  setLanguagesVisible: () => {},
  mobilePanelID: "",
  setMobilePanelID: () => {},
  desktopPanelID: "",
  setDesktopPanelID: () => {},
  mobileHeaderHeight: 0,
  setMobileHeaderHeight: () => {},
  desktopHeaderHeight: 0,
  setDesktopHeaderHeight: () => {},
});
const NavigationProvider: FC<Props> = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [transitionComplete, setTransitionComplete] = useState<boolean>(true);
  const [settingsVisible, setSettingsVisible] = useState<boolean>(false);
  const [languagesVisible, setLanguagesVisible] = useState<boolean>(false);
  const [panelOpen, setPanelOpen] = useState<boolean>(false);
  const [mobilePanelID, setMobilePanelID] = useState<string>("");
  const [desktopPanelID, setDesktopPanelID] = useState<string>("");
  const [mobileHeaderHeight, setMobileHeaderHeight] = useState<number>(0);
  const [desktopHeaderHeight, setDesktopHeaderHeight] = useState<number>(0);
  const { isMd } = useBreakPoint("md");
  const { isSm } = useBreakPoint("sm");

  useEffect(() => {
    if (isSm && !isMd) {
      setDesktopPanelID("");
    }
    if (isMd) {
      setMobileMenuOpen(false);
      setMobilePanelID("");
    }
  }, [isMd, isSm]);
  useEffect(() => {
    setPanelOpen(mobileMenuOpen || !!mobilePanelID || !!desktopPanelID || settingsVisible || languagesVisible);
  }, [mobileMenuOpen, mobilePanelID, desktopPanelID, settingsVisible, languagesVisible]);
  useEffect(() => {
    if (panelOpen && transitionComplete) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
  }, [panelOpen, isMd, transitionComplete]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--mobile-header", `${mobileHeaderHeight}px`);
  }, [mobileHeaderHeight]);
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--desktop-header", `${desktopHeaderHeight}px`);
  }, [desktopHeaderHeight]);
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--header-height", `${isMd ? desktopHeaderHeight : mobileHeaderHeight}px`);
  }, [mobileHeaderHeight, desktopHeaderHeight, isMd]);
  return (
    <NavigationContext.Provider
      value={{
        mobileMenuOpen,
        setMobileMenuOpen,
        transitionComplete,
        setTransitionComplete,
        settingsVisible,
        setSettingsVisible,
        languagesVisible,
        setLanguagesVisible,
        mobilePanelID,
        setMobilePanelID,
        desktopPanelID,
        setDesktopPanelID,
        mobileHeaderHeight,
        setMobileHeaderHeight,
        desktopHeaderHeight,
        setDesktopHeaderHeight,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};
export default NavigationProvider;
